import moment from 'moment';
import {fudaoAppService} from 'langyan';
import fudaoApi from '@/api/fudao';
import userApi from '@/api/user';
import knowledgeApi from '@/api/knowledge';
import {subscribeFudaoEvents} from './event-handler';

let DATE_FORMAT = 'YYYY-MM-DDTHH:mm:ss.SSSZ';

export default {
  initFudaoService({commit, state, rootState, dispatch}, payload) {
    subscribeFudaoEvents(commit, state, rootState, dispatch);
  },

  // 邀請輔導 ---------------------------------------------------

  async inviteFudao({commit, state, rootState, dispatch}, payload) {
    if (!rootState.isTeacher) {
      dispatch('alert/addError', '無法邀請輔導: 角色錯誤', {root: true});
      return;
    }
    if (!rootState.chat.currentChat.id) {
      dispatch('alert/addError', '無法邀請輔導: 目標學生錯誤', {root: true});
      return;
    }

    // find target student username
    let chatDto = rootState.chat.currentChat;
    let signinUsername = rootState.profile.username;
    let participants = Array.from(chatDto.participants.values());
    let studentUsername = participants[0] !== signinUsername ? participants[0] : participants[1];
    if (!studentUsername) {
      dispatch('alert/addError', '無法邀請輔導: invalid username', {root: true});
      return;
    }

    let studentProfile;
    // find student's profile from store
    for (let key in state.profiles) {
      if (!state.profiles.hasOwnProperty(key)) {
        continue;
      }
      let profile = state.profiles[key];
      if (profile.username === studentUsername) {
        studentProfile = profile;
        break;
      }
    }

    // profile is not in store, so send request to get student profile
    if (!studentProfile) {
      studentProfile = await userApi.getProfileByUsername(studentUsername);
    }
    if (!studentProfile || !studentProfile._id) {
      dispatch('alert/addError', '無法邀請輔導: invalid profile', {root: true});
      return;
    }

    // save profile to store
    commit('updateProfile', studentProfile);
    return await fudaoAppService.inviteFudaoAsync(studentProfile._id);
  },

  // 延長輔導 --------------------------------------------------------


  async acceptExtendFudaoInvition({commit, state, rootState, dispatch}, payload) {
    if (!state.fudaoDto.id) {
      return;
    }
    await fudaoAppService.acceptExtendFudaoInvition(state.fudaoDto.id);
  },

  async rejectExtendFudaoInvition({commit, state, rootState, dispatch}, payload) {
    if (!state.fudaoDto.id) {
      return;
    }
    await fudaoAppService.rejectExtendFudaoInvition(state.fudaoDto.id);
  },


  // ---------------------------------------------------------

  async getFudaoHistory({commit, state, rootState, dispatch}, payload) {
    // three months ago
    let startDate = moment().add(-3, 'months').set({
      'hour': 0,
      'minute': 0,
      'second': 0,
      'millisecond': 0
    });

    let params = {startTime_after: startDate.toISOString()};
    let paramIdKey = rootState.isTeacher ? 'teacherId' : 'studentId';
    params[paramIdKey] = rootState.userid;

    let res = await fudaoApi.getFudaos(params);
    let fudaos = res.fudaos || [];

    let idSet = new Set();
    let targetKey = rootState.isTeacher ? 'studentId' : 'teacherId';

    fudaos = fudaos.map(f => {
      let subjects;
      if (f.teacherForm) {
        subjects = f.teacherForm.fudaoSubjects || [];
        /* eslint-disable indent */
        subjects = subjects
          .filter(s => rootState.subjects[s] !== undefined)
          .map(s => rootState.subjects[s].tname)
          .join('/');
      }

      let startTime = moment(f.startTime, DATE_FORMAT);
      let endTime = f.endTime ? moment(f.endTime, DATE_FORMAT) : null;

      let duration;
      if (endTime) {
        duration = moment.duration(endTime.diff(startTime));
        let minutes = duration.get('minutes');
        let seconds = duration.get('seconds');
        seconds = seconds < 10 ? '0' + seconds : seconds;
        duration = minutes + ':' + seconds;
      }

      let teacherId = f.teacherId;
      let targetName;
      let targetAvatar;
      let targetId = f[targetKey];
      // get profile from store. if it is not exist, put id to idSet and then call api to get it.
      let targetProfile = state.profiles[targetId];
      if (targetProfile) {
        targetName = targetProfile.nickname || targetProfile.username;
        targetAvatar = targetProfile.avatar;
      } else {
        idSet.add(targetId);
      }

      let date = startTime.format('YYYY-MM-DD');
      let fudaoId = f._id;

      let teacherReportStatus;
      if (f.teacherForm) {
        teacherReportStatus = '已填寫';
      } else {
        // fudao status: Created, Started, Ended, AbnormalEnded(未正常結束)
        teacherReportStatus = f.status === 'AbnormalEnded' ? '未正常結束' : '未填寫';
      }

      return {fudaoId, teacherId, targetId, targetName, targetAvatar, date, subjects, duration, teacherReportStatus};
    });

    // get profiles which does not exist in store.
    let profiles = await dispatch('getProfiles', idSet);
    fudaos.forEach(f => {
      if (f.targetName) {
        return;
      }

      let targetProfile = profiles[f.targetId];
      let targetName = targetProfile.nickname || targetProfile.username;
      f.targetName = targetName;
      f.targetAvatar = targetProfile.avatar;
    });

    return fudaos;
  },

  async getProfiles({commit, state, rootState}, idSet) {
    let ids = Array.from(idSet.values());
    let promises = [];
    ids.forEach(id => promises.push(userApi.getProfileByID(id)));
    let responses = await Promise.all(promises);

    let profiles = {};
    responses.forEach(r => {
      if (!r.success) {
        return;
      }
      profiles[r.profile._id] = r.profile;
    });

    commit('updateProfiles', profiles);
    return profiles;
  },

  async getProfileById({commit, state, rootState}, id) {
    if (state.profiles[id]) {
      return state.profiles[id];
    }
    let response = await userApi.getProfileByID(id);
    if (!response.success) {
      return {};
    }
    commit('updateProfile', response.profile);
    return response.profile;
  },

  async getKnowledges({commit, state, rootState}, payload) {
    let res = await knowledgeApi.getKnowledges(payload);
    if (!res.success) {
      throw new Error('failed to query knowledges', res);
    }

    return res.knowledge;
  },

  async reportTeacherForm({commit, state, rootState}, payload) {
    await fudaoApi.reportTeacherForm(payload);
  },

  async reportStudentForm({commit, state, rootState}, payload) {
    await fudaoApi.reportStudentForm(payload);
  }
};